import { Component } from '@theme/component';
import {
  debounce,
  getAnnouncementBarVisibleBlockSizePx,
  onAnimationEnd,
  prefersReducedMotion,
} from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLButtonElement} [viewAllButton] - View all results button in the modal footer.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  get #showRecentlyViewed() {
    return this.dataset.showRecentlyViewed === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      window.addEventListener('resize', this.#debouncedSyncSearchModalAnnouncement, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.addEventListener('click', this.#handlePredictiveSearchTabClick, { signal });

    if (this.#showRecentlyViewed && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * @returns {'products' | 'articles'}
   */
  #getActiveSearchTab() {
    const panels = this.querySelector('.predictive-search-results__panels');
    if (panels instanceof HTMLElement && panels.dataset.activeTab) {
      return panels.dataset.activeTab === 'articles' ? 'articles' : 'products';
    }

    const selectedTab = this.querySelector('.predictive-search-results__tab.selected[data-tab]');
    if (selectedTab instanceof HTMLElement) {
      const name = selectedTab.getAttribute('data-tab');
      if (name === 'articles' || name === 'products') {
        return name;
      }
    }

    const hasProducts = Boolean(this.querySelector('#predictive-search-products'));
    const hasArticles = Boolean(this.querySelector('#predictive-search-articles'));

    if (hasArticles && !hasProducts) {
      return 'articles';
    }

    return 'products';
  }

  handleViewAllClick = (/** @type {MouseEvent} */ event) => {
    const button = this.refs.viewAllButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const productUrl = (button.dataset.productViewAllUrl || '').trim();
    const articleUrl = (button.dataset.articleViewAllUrl || '').trim();

    if (!productUrl && !articleUrl) {
      return;
    }

    event.preventDefault();

    const q = this.refs.searchInput?.value?.trim() ?? '';
    const activeTab = this.#getActiveSearchTab();
    const tabUrl = activeTab === 'articles' ? articleUrl : productUrl;
    const base = tabUrl || Theme.routes.search_url;

    const url = new URL(base, location.origin);
    if (q) {
      url.searchParams.set('q', q);
    }

    window.location.href = url.toString();
  };

  #handlePredictiveSearchTabClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('.predictive-search-results__tab[data-tab]');
    if (!(tab instanceof HTMLButtonElement)) return;

    const tabName = tab.getAttribute('data-tab');
    if (tabName !== 'products' && tabName !== 'articles') return;

    const productsEl = this.querySelector('#predictive-search-products');
    const articlesEl = this.querySelector('#predictive-search-articles');
    if (!productsEl && !articlesEl) return;

    if (tabName === 'products') {
      if (productsEl) productsEl.removeAttribute('hidden');
      if (articlesEl) articlesEl.setAttribute('hidden', '');
    } else {
      if (productsEl) productsEl.setAttribute('hidden', '');
      if (articlesEl) articlesEl.removeAttribute('hidden');
    }

    const panels = this.querySelector('.predictive-search-results__panels');
    if (panels instanceof HTMLElement) {
      panels.dataset.activeTab = tabName;
    }

    for (const tabEl of this.querySelectorAll('.predictive-search-results__tab[data-tab]')) {
      if (!(tabEl instanceof HTMLButtonElement)) continue;
      const name = tabEl.getAttribute('data-tab');
      const isActive = name === tabName;
      tabEl.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tabEl.classList.toggle('selected', isActive);
    }

    this.#currentIndex = -1;
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#clearSearchModalAnnouncementOffset();
    this.#resetSearch();
  };

  #debouncedSyncSearchModalAnnouncement = debounce(() => {
    this.#syncSearchModalAnnouncementOffset();
  }, 50);

  #syncSearchModalAnnouncementOffset = () => {
    const dialogComponent = this.dialog;
    if (!dialogComponent || dialogComponent.id !== 'search-modal') return;

    const dialogEl = /** @type {DialogComponent} */ (dialogComponent).refs?.dialog;
    if (!(dialogEl instanceof HTMLDialogElement) || !dialogEl.open) return;

    const px = getAnnouncementBarVisibleBlockSizePx();
    dialogComponent.style.setProperty('--announcement-bar-visible-height', `${px}px`);
  };

  #clearSearchModalAnnouncementOffset = () => {
    if (this.dialog?.id !== 'search-modal') return;
    this.dialog.style.removeProperty('--announcement-bar-visible-height');
  };

  #handleDialogOpen = () => {
    this.#syncSearchModalAnnouncementOffset();

    if (this.dialog?.id === 'search-modal') {
      const dialogEl = /** @type {DialogComponent} */ (this.dialog).refs?.dialog;
      if (dialogEl instanceof HTMLDialogElement && dialogEl.open) {
        requestAnimationFrame(() => {
          this.refs.searchInput?.focus({ preventScroll: true });
        });
      }
    }

    if (this.#showRecentlyViewed && !this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter(
        (item) =>
          item instanceof HTMLElement &&
          !item.closest('[hidden]')
      );

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * Tab / Shift+Tab are not intercepted so focus can move through the dialog (native dialog focus order).
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      const active = document.activeElement;
      if (active instanceof HTMLElement) {
        const tab = active.closest('.predictive-search-results__tab[data-tab]');
        if (tab instanceof HTMLButtonElement) {
          event.preventDefault();
          event.stopPropagation();
          tab.click();
          return;
        }

        const chromeButton = active.closest(
          '.js-predictive-search-reset, .js-predictive-search-close-modal, .predictive-search__search-button'
        );
        if (chromeButton instanceof HTMLButtonElement && !chromeButton.hidden) {
          event.preventDefault();
          event.stopPropagation();
          chromeButton.click();
          return;
        }
      }
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content-wrapper')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'query,product,article');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#restoreFocusInsideDialogIfNeeded();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #restoreFocusInsideDialogIfNeeded() {
    const dialogEl = this.closest('dialog');
    if (!(dialogEl instanceof HTMLDialogElement) || !dialogEl.open) return;
    if (dialogEl.contains(document.activeElement)) return;
    this.refs.searchInput?.focus({ preventScroll: true });
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (this.#showRecentlyViewed && viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    this.#restoreFocusInsideDialogIfNeeded();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
